/**
 * Compare data model — central Zod schemas + inferred TypeScript types.
 *
 * Phase 44 foundation. Every `lib/compare/products/*.ts` and `lib/compare/pages/*.ts`
 * module is parsed by these schemas at module load (see lib/compare/products/index.ts
 * and lib/compare/pages/index.ts). Parse failures throw at import time, which causes
 * `next build` and `tsc --noEmit` to fail — exactly the build-time gate DATA-03,
 * DATA-04, DATA-05, and DATA-06 require.
 *
 * Shape rationale is documented in:
 *   .planning/phases/44-compare-data-model-foundation/44-RESEARCH.md
 *
 * Do not add new runtime dependencies. Only zod (4.3.6) is allowed here.
 */

import { z } from "zod";

// ────────────────────────────────────────────────────────────────────────
// sourceBasis (44-RESEARCH.md recommended shape — Array<{fact,url,fetchedAt,note?}>)
// ────────────────────────────────────────────────────────────────────────

/**
 * One entry in a product's sourceBasis — ties a named factual claim to the
 * source URL it was pulled from and the date the source was observed.
 *
 * `fact` is free text (not an enum) so new fact keys like "feature:CPP",
 * "weakness:noMobile", "externalRating:g2" can be added without a schema
 * migration. Collisions/typos are an open question tracked in 44-RESEARCH.md.
 */
export const sourceBasisEntrySchema = z.object({
  fact: z.string().min(1, "sourceBasis entry must name the fact it backs"),
  url: z.string().url("sourceBasis entry must carry a real source URL"),
  fetchedAt: z.string().date(),
  note: z.string().max(200).optional(),
});

export type SourceBasisEntry = z.infer<typeof sourceBasisEntrySchema>;

/**
 * DATA-06 — every product has at least one sourceBasis entry with a source URL.
 * Day-one records can carry a single vendor-homepage entry; Phase 48 flagships
 * will grow to 3–10 entries. Zero-entry records fail the build.
 */
export const sourceBasisSchema = z
  .array(sourceBasisEntrySchema)
  .min(1, "every product needs at least one sourceBasis entry (DATA-06)");

// ────────────────────────────────────────────────────────────────────────
// externalRating (DATA-01 optional; Phase 45 AEO-05 aggregateRating gate)
// ────────────────────────────────────────────────────────────────────────

/**
 * Third-party rating. Always exactly one platform/number/URL. Used by Phase 45
 * to emit `aggregateRating` JSON-LD — no externalRating means no JSON-LD, which
 * is the correct behaviour (AEO-05: "never self-awarded").
 */
export const externalRatingSchema = z.object({
  value: z.number().min(0).max(10),
  outOf: z.number().min(1).max(10),
  source: z.string().min(1), // "G2", "Capterra", "Trustpilot", etc.
  sourceUrl: z.string().url(),
  fetchedAt: z.string().date(),
  // Phase 21 (TRUST-03) — real aggregate review count from the third-party
  // source; never a stand-in "1" (AEO-05). Optional so existing records (none
  // populated today) parse unchanged; jsonld emits ratingCount only when set.
  reviewCount: z.number().int().positive().optional(),
});

export type ExternalRating = z.infer<typeof externalRatingSchema>;

// ────────────────────────────────────────────────────────────────────────
// BusinessSize (HUB-04 — shared segment taxonomy)
// ────────────────────────────────────────────────────────────────────────
// Used by CompareProduct.fit.businessSizes, ComparePage.businessSizes,
// ComparePage.segmentedVerdicts[].businessSize, and the hub filter UI.
// One identifier across every surface.
export const BUSINESS_SIZE = [
  "sole-trader",
  "2-5",
  "6-20",
  "20-100",
  "100-plus",
] as const;
export const businessSizeSchema = z.enum(BUSINESS_SIZE);
export type BusinessSize = z.infer<typeof businessSizeSchema>;

export const BUSINESS_SIZE_LABEL: Record<BusinessSize, string> = {
  "sole-trader": "Sole trader",
  "2-5": "2–5 person team",
  "6-20": "6–20 person firm",
  "20-100": "20–100 person firm",
  "100-plus": "100+ enterprise",
};

// ────────────────────────────────────────────────────────────────────────
// Feature cell — status + optional note, keyed by feature name
// ────────────────────────────────────────────────────────────────────────

/**
 * Per-feature snapshot. Deliberately lightweight — sources live in the sibling
 * `sourceBasis` array, not embedded per cell (see 44-RESEARCH.md alternatives D).
 */
export const featureCellSchema = z.object({
  status: z.enum(["yes", "no", "partial"]),
  note: z.string().max(200).optional(),
});

export type FeatureCell = z.infer<typeof featureCellSchema>;

// ────────────────────────────────────────────────────────────────────────
// CompareProduct (DATA-01, DATA-03, DATA-05, DATA-06)
// ────────────────────────────────────────────────────────────────────────

/**
 * A competitor or product being compared. Fields map 1:1 to DATA-01.
 *
 * Build-time gates (Phase 44 success criteria):
 *   - >=2 strengths            (DATA-03 floor; upper bound 6 to force tight writing)
 *   - >=1 weakness             (DATA-03 + DATA-05 — mandatory even for The Site Book)
 *   - >=1 sourceBasis entry    (DATA-06)
 *   - reviewedAt present       (DATA-01, FRESH-01)
 *
 * Fields marked optional here are optional per DATA-01 (externalRating only).
 * Everything else is required at build time.
 */
export const compareProductSchema = z.object({
  slug: z
    .string()
    .min(1)
    .regex(/^[a-z0-9-]+$/, "slug must be kebab-case lowercase"),
  name: z.string().min(1),
  vendor: z.string().min(1),
  url: z.string().url(),

  pricing: z.object({
    headline: z.string().min(1),
    model: z.enum(["flat", "per-seat", "per-document", "enterprise", "freemium"]),
    publicPriceKnown: z.boolean(),
    /**
     * #716 — opt-in, authoritative structured price for JSON-LD. When present,
     * `offerForProduct()` in lib/compare/jsonld.ts uses this instead of parsing
     * the headline, restoring Product rich-result eligibility for products
     * whose marketing headline is too ambiguous to parse truthfully (ranges
     * with mixed monthly+annual cadence, additive setup fees).
     *
     * Shape → JSON-LD mapping:
     *   - `high` present            → AggregateOffer (lowPrice/highPrice)
     *   - `openEnded: true`         → AggregateOffer (lowPrice only — a
     *                                 published floor with no ceiling,
     *                                 e.g. "from £675/site")
     *   - neither                   → single flat Offer with `price`
     *   - `recurrence` month/year   → a UnitPriceSpecification carries the
     *                                 billing cadence so /mo and /yr prices
     *                                 are modelled, not dropped
     */
    offer: z
      .object({
        low: z.number().nonnegative(),
        high: z.number().nonnegative().optional(),
        openEnded: z.boolean().optional(),
        currency: z.literal("GBP").default("GBP"),
        recurrence: z.enum(["month", "year", "one-off"]).optional(),
      })
      .refine((o) => o.high === undefined || o.high >= o.low, {
        message: "pricing.offer.high must be >= low",
      })
      .refine((o) => !(o.high !== undefined && o.openEnded === true), {
        message:
          "pricing.offer cannot be both bounded (high) and openEnded — pick one",
      })
      .optional(),
  }),

  fit: z.object({
    bestFor: z.string().min(10),
    teamSize: z.string().min(1),
    sweetSpot: z.string().min(10),
    businessSizes: z.array(businessSizeSchema).min(1),
  }),

  /**
   * First-party Help Centre evidence behind product claims. Competitor records
   * deliberately leave this empty: we link their evidence through sourceBasis
   * instead of pretending their support content belongs to us.
   */
  helpLinks: z
    .array(
      z.object({
        href: z
          .string()
          .regex(/^\/help\//, "product help links must use a /help/ path"),
        label: z.string().min(3).max(80),
        description: z.string().min(10).max(180),
      }),
    )
    .min(1)
    .max(5)
    .optional(),

  features: z.record(z.string().min(1), featureCellSchema),

  strengths: z
    .array(z.string().min(10))
    .min(2, "every product needs >=2 strengths (DATA-03)")
    .max(6, "strengths should be tightly written — keep the list <=6"),

  weaknesses: z
    .array(z.string().min(10))
    .min(1, "every product needs >=1 weakness, including The Site Book (DATA-05)")
    .max(6),

  sourceBasis: sourceBasisSchema,

  reviewedAt: z.string().date(),

  externalRating: externalRatingSchema.optional(),
});

export type CompareProduct = z.infer<typeof compareProductSchema>;

/** #716 — the declared structured price, when a product opts in. */
export type DeclaredPriceOffer = NonNullable<
  CompareProduct["pricing"]["offer"]
>;

// ────────────────────────────────────────────────────────────────────────
// ComparePage (DATA-02, DATA-04)
// ────────────────────────────────────────────────────────────────────────

/**
 * FAQ item shape used on compare pages. Compatible with lib/faq-data.ts so
 * Phase 45 can feed it to `faqJsonLd` without a second type definition.
 *
 * AEO-01: every compare-page FAQ answer must sit in a 50–80 word window
 * (inclusive). The word counter trims, collapses whitespace, splits on a
 * single space, and filters empty-string tokens — so "foo  bar\nbaz" counts
 * as 3 words. Dashes and HTML entities are part of their surrounding word.
 *
 * The .refine() runs at module load inside comparePageSchema.parse — a
 * drifted answer throws and fails `next build` / `tsc --noEmit`, which is
 * the only enforcement mechanism we trust long-term.
 */
export function countAnswerWords(answer: string): number {
  const normalized = answer.trim().replace(/\s+/g, " ");
  if (normalized.length === 0) return 0;
  return normalized.split(" ").filter((token) => token.length > 0).length;
}

export const compareFaqItemSchema = z.object({
  question: z.string().min(5),
  answer: z
    .string()
    .min(20)
    .refine(
      (a) => {
        const n = countAnswerWords(a);
        return n >= 50 && n <= 80;
      },
      {
        message:
          "compare FAQ answer must be 50–80 words (AEO-01); count is off",
      },
    ),
});

export type CompareFaqItem = z.infer<typeof compareFaqItemSchema>;

/**
 * Segmented verdict — a named audience slice with its own verdict string.
 * DATA-02 lists this as `segmentedVerdicts` — used on roundup and alternatives
 * pages where the "winner" depends on the reader's situation.
 */
export const segmentedVerdictSchema = z.object({
  segment: z.string().min(3), // e.g. "Solo builders", "10–50 seat firms"
  /** Optional visible heading when two audience slices recommend one product. */
  heading: z.string().min(5).max(80).optional(),
  verdict: z.string().min(20),
  recommendedProductSlug: z.string().min(1).optional(),
  businessSize: businessSizeSchema.optional(),
});

export type SegmentedVerdict = z.infer<typeof segmentedVerdictSchema>;

/**
 * A compare page — one per /compare/* URL. DATA-02 field list is exhaustive;
 * build-time gates from Phase 44 success criteria:
 *   - verdict present           (DATA-04)
 *   - >=3 faq items             (DATA-04)
 *   - >=3 relatedPageSlugs      (DATA-04)
 *   - reviewedAt present        (FRESH-01)
 *
 * Phase 67 (COMPARE-01) — citation-grade upgrade adds two optional fields
 * plus a cross-field refine:
 *   - `directAnswer` — 40–80 word direct-answer paragraph rendered above the
 *     fold under the verdict. Word count reuses the existing countAnswerWords
 *     helper so there is one word-counting contract on the whole file.
 *   - `citationGrade` — boolean flag declaring the page has been upgraded
 *     to the citation-grade format kit. When set to true, the top-level
 *     `.superRefine()` asserts that directAnswer is present and in window;
 *     mis-count or missing field throws at module load, failing next build
 *     and tsc --noEmit.
 */
export const comparePageSchema = z
  .object({
  slug: z
    .string()
    .min(1)
    .regex(/^[a-z0-9-]+$/, "compare page slug must be kebab-case lowercase"),
  kind: z.enum(["vs", "roundup", "alternatives"]),
  productSlugs: z
    .array(z.string().min(1))
    .min(1, "a compare page must reference at least one product"),
  verdict: z
    .string()
    .min(20, "verdict must be a real sentence — no empty string (DATA-04)"),
  segmentedVerdicts: z.array(segmentedVerdictSchema).default([]),
  // AEO-01 — every compare page ships 4–8 FAQ items. This was Phase 44
  // .min(3) (DATA-04 floor); tightened to .min(4).max(8) in Phase 45 so
  // the FAQPage JSON-LD emitted by lib/compare/jsonld.ts satisfies the
  // AEO-01 count gate without runtime filtering.
  faq: z
    .array(compareFaqItemSchema)
    .min(4, "every compare page needs >=4 FAQ items (AEO-01)")
    .max(8, "every compare page needs <=8 FAQ items (AEO-01)"),
  relatedPageSlugs: z
    .array(z.string().min(1))
    .min(3, "every compare page needs >=3 related-page links (DATA-04)"),
  relatedGuideSlug: z.string().min(1).optional(),
  supportingLinks: z
    .array(
      z.object({
        href: z.string().regex(/^\//, "supporting links must be internal paths"),
        label: z.string().min(3).max(80),
        description: z.string().min(10).max(180),
      }),
    )
    .max(6)
    .optional(),
  reviewedAt: z.string().date(),
  nextReviewBy: z.string().date().optional(),
  contentOwner: z.string().min(3).optional(),
  reviewedBy: z.string().min(3).optional(),
  approvedAt: z.string().date().optional(),
  materialChanges: z.array(z.string().min(3)).optional(),
  seoTitle: z.string().min(10).max(70),
  citableSummary: z
    .string()
    .min(50, "citableSummary must be substantive (>=50 chars)")
    .max(600, "citableSummary must be lift-friendly for AI engines (<=600 chars)"),
  evidenceNote: z.string().min(20),

  /**
   * Phase 50 (HUB-04) — top-level business-size tag array used by the hub
   * filter, coverage-cap test, and JSON-LD aggregator. Required — every page
   * must declare which audience sizes it serves so the hub filter UI has a
   * deterministic grouping axis. Enum refinement (via businessSizeSchema)
   * throws at module load on any typo or unknown value.
   */
  businessSizes: z.array(businessSizeSchema).min(1),

  /**
   * Phase 48 (MIGRATE-01..05) — short founder-voice narrative paragraph
   * (2–4 sentences). Optional; only the 4 flagship pages populate this
   * in Phase 48, the remaining 8 pages leave it undefined and the shell
   * skips rendering. Added here as an additive, backward-compatible
   * extension so existing 12 pages keep passing validation unchanged.
   */
  narrative: z.string().min(20).max(800).optional(),

  /**
   * Phase 48 (MIGRATE-01..05) — 3–6 "Why switch from X" bullet points,
   * used by ComparePageShell to render a compact list inside the
   * BottomLineVerdict / ProductBreakdown area. Optional; only the 4
   * flagship pages populate this in Phase 48.
   */
  whySwitch: z
    .array(z.string().min(10).max(200))
    .min(3, "whySwitch needs >=3 bullets when set")
    .max(6, "whySwitch needs <=6 bullets when set")
    .optional(),

  /**
   * Phase 67 (COMPARE-01) — 40–80 word direct-answer paragraph rendered
   * above the fold under the one-sentence verdict on the 5 upgraded
   * compare pages. Optional at the field level so the 12 non-citation-grade
   * pages still parse unchanged; made mandatory by the cross-field refine
   * below whenever `citationGrade === true`. Word count reuses the
   * existing `countAnswerWords` helper — see note on that helper above.
   */
  directAnswer: z
    .string()
    .min(20)
    .refine(
      (s) => {
        const n = countAnswerWords(s);
        return n >= 40 && n <= 80;
      },
      {
        message: "directAnswer must be 40–80 words (COMPARE-01)",
      },
    )
    .optional(),

  /**
   * Phase 67 (COMPARE-01) — declares this page has been upgraded to the
   * citation-grade format kit (direct-answer lead + ItemList + ≥5 FAQs +
   * Person/Organization author). Used by:
   *   - the cross-field .superRefine() below to require directAnswer
   *   - lib/compare/jsonld.ts buildComparePageJsonLd to emit ItemList on
   *     vs-kind pages when citationGrade === true
   *
   * Optional at the field level so the 12 non-citation-grade compare
   * pages (and fixtures) parse unchanged without having to declare the
   * field. Consumers treat `undefined` and `false` as identical — see
   * the `page.citationGrade === true` strict-equals check at every
   * call-site (lib/compare/jsonld.ts, superRefine below).
   */
  citationGrade: z.boolean().optional(),

  /**
   * Phase 18 (LEAK-01) — product-aware primary CTA selector. Optional so the
   * 26 existing non-Site-Control pages (incl. the 13 RAMS pages) parse and
   * render byte-identical. "site-control" opts a page into the quote-led
   * "Book a walkthrough" CTA. Consumed ONLY via resolveCta() in lib/compare/cta.ts.
   */
  ctaVariant: z.enum(["default", "site-control"]).optional(),
})
  .superRefine((page, ctx) => {
    // COMPARE-01 — citationGrade pages must carry a 40–80 word directAnswer.
    // The field-level refine on directAnswer enforces the window when the
    // value is present; this refine ensures the value is not omitted on a
    // page that has flipped citationGrade: true.
    if (page.citationGrade === true) {
      if (
        page.directAnswer === undefined ||
        countAnswerWords(page.directAnswer) < 40 ||
        countAnswerWords(page.directAnswer) > 80
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            "citationGrade pages must carry a 40–80 word directAnswer (COMPARE-01)",
          path: ["directAnswer"],
        });
      }
    }
  });

export type ComparePage = z.infer<typeof comparePageSchema>;
